import { useId } from 'react';
import type { ReactNode } from 'react';
import { Card } from '@shared/components';
import { DueDateLabel } from './DueDateLabel';
import type { StillToInvoiceAddOnRow, StillToInvoicePanelModel, StillToInvoiceServiceRow } from '../utils/stillToInvoicePanel';

interface Props {
    panel: StillToInvoicePanelModel;
}

function Group({ title, empty, isEmpty, children }: { title: string; empty: string; isEmpty: boolean; children: ReactNode }) {
    const headingId = useId();
    return (
        <section aria-labelledby={headingId} className="space-y-2">
            <h5 id={headingId} className="text-xs font-semibold uppercase tracking-wide text-mp-muted">
                {title}
            </h5>
            {isEmpty ? <p className="text-sm text-mp-muted">{empty}</p> : <ul className="divide-y divide-mp-border">{children}</ul>}
        </section>
    );
}

function ServiceRow({ row }: { row: StillToInvoiceServiceRow }) {
    return (
        <li className="flex items-start justify-between gap-3 py-2">
            <div className="min-w-0">
                <p className="text-sm text-mp-foreground">{row.name}</p>
                {row.note && <p className="text-xs text-mp-muted">{row.note}</p>}
            </div>
            <div className="shrink-0 text-right">
                <p className="text-sm font-semibold text-mp-foreground tabular-nums">{row.net}</p>
                {row.due.date && <DueDateLabel date={row.due.date} overdue={row.overdue} className="flex justify-end text-xs" />}
                {row.due.mileage && <p className="text-xs text-mp-muted">{row.due.mileage}</p>}
            </div>
        </li>
    );
}

function AddOnRow({ row }: { row: StillToInvoiceAddOnRow }) {
    return (
        <li className="flex items-start justify-between gap-3 py-2">
            <div className="min-w-0">
                <p className="text-sm text-mp-foreground">
                    {row.name}
                    {row.quantity && <span className="ml-1 text-mp-muted">{row.quantity}</span>}
                </p>
                {row.linkedTo && <p className="text-xs text-mp-muted">Linked to {row.linkedTo}</p>}
            </div>
            <div className="shrink-0 text-right">
                <p className="text-sm font-semibold text-mp-foreground tabular-nums">{row.net}</p>
                {row.dueDate && <DueDateLabel date={row.dueDate} overdue={row.overdue} className="flex justify-end text-xs" />}
            </div>
        </li>
    );
}

export function StillToInvoicePanel({ panel }: Props) {
    return (
        <Card className="space-y-4 px-4 py-4">
            <Group title="Services" empty={panel.emptyMessages.services} isEmpty={panel.services.length === 0}>
                {panel.services.map((row) => (
                    <ServiceRow key={row.id} row={row} />
                ))}
            </Group>
            <Group title="Optional Extras" empty={panel.emptyMessages.addOns} isEmpty={panel.addOns.length === 0}>
                {panel.addOns.map((row) => (
                    <AddOnRow key={row.id} row={row} />
                ))}
            </Group>
            <div className="flex items-center justify-between border-t border-mp-border pt-3 text-sm font-semibold text-mp-foreground">
                <span>Total ex VAT</span>
                <span className="tabular-nums">{panel.totalNet}</span>
            </div>
        </Card>
    );
}
